import { useEffect, useState } from 'react';
import {
  CLAUDE_DESIGN_HANDOFF_STEPS,
  CLAUDE_DESIGN_IMPORTED_MESSAGE,
  CLAUDE_DESIGN_OFFER_INTRO,
  CLAUDE_DESIGN_SETUP_STEPS,
  CLAUDE_DESIGN_SKIP_MESSAGE,
  CLAUDE_DESIGN_UPLOAD_REQUEST,
  getEffectiveClaudeDesignExportEnabled,
} from '@blueprint/shared';
import { apiFetch } from '../../lib/api';
import { downloadClaudeDesignZip, fetchClaudeDesignPrompt } from '../../lib/claudeDesignExport';
import { useMyTenant } from '../../hooks/useMyTenant';
import ClaudeDesignBundleUpload from '../ClaudeDesignBundleUpload';

// #3704 — the Claude Design round trip in the UX Design agent's chat (AVR rulings, issue #3704
// comment 6400159787, ruling 4): Export ZIP, Copy PROMPT.md, the fixed setup and handoff steps, an
// in-chat bundle upload, and Skip as an equally visible choice.
//
// mode "offer": shown under the offer (the done frame's claudeDesignOffer, or the session's
//   claudeDesignRoundTrip.offeredAt on reload). The export buttons follow the effective
//   claude_design_export_enabled flag, fail closed until it is known (ruling 7).
// mode "upload": shown under the agent's request for a seed bundle; upload only.
//
// After an upload the card sends a fixed message so the agent confirms the import at once
// (ruling 5); the upload component itself then offers Approve & Merge.

interface Props {
  /** The session's project id (R3 P3-3: not the URL parameter). Anything but a UUID disables the actions. */
  projectId: string;
  mode: 'offer' | 'upload';
  /** May return a promise; Skip stays disabled until it settles (R1 P2-1). */
  onSendMessage: (content: string) => void | Promise<void>;
  /** Read-only viewers see the steps, not the actions. */
  readOnly?: boolean;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const btn = 'text-xs font-medium px-3 py-1.5 rounded-full transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed';

export default function ClaudeDesignRoundTripCard({ projectId, mode, onSendMessage, readOnly }: Props) {
  const { myTenant } = useMyTenant();
  const [override, setOverride] = useState<boolean | null>(null);
  const [overrideLoaded, setOverrideLoaded] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [uploadOpen, setUploadOpen] = useState(mode === 'upload');
  const [skipping, setSkipping] = useState(false);
  const validProject = UUID_RE.test(projectId);

  useEffect(() => {
    if (mode !== 'offer' || !validProject) return;
    let cancelled = false;
    apiFetch<{ claudeDesignExportEnabled: boolean | null }>(`/projects/${projectId}`)
      .then(p => { if (!cancelled) { setOverride(p.claudeDesignExportEnabled ?? null); setOverrideLoaded(true); } })
      .catch(() => { if (!cancelled) setOverrideLoaded(false); });
    return () => { cancelled = true; };
  }, [projectId, mode, validProject]);

  // FAIL-CLOSED: no export buttons until both the project override and the tenant default are known.
  const exportEnabled = mode === 'offer' && validProject && overrideLoaded && !!myTenant
    && getEffectiveClaudeDesignExportEnabled(override, myTenant.claudeDesignExportEnabled);

  const handleExport = async () => {
    setExporting(true);
    setError('');
    try {
      await downloadClaudeDesignZip(projectId, 'project');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Claude Design export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleCopy = async () => {
    setError('');
    try {
      await navigator.clipboard.writeText(await fetchClaudeDesignPrompt(projectId));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Copy failed');
    }
  };

  // R1 P2-1 / final delta P2-1 — the card never hides itself. ChatPage hides it only when the
  // server's done frame confirms the skip was recorded (claudeDesignSkipped); a failed send
  // (handleSend reports every failure as a page error and resolves) or a queued one (202) never
  // reaches `done`, so the card stays. Skip is disabled while its send is in flight.
  const handleSkip = async () => {
    if (skipping) return;
    setSkipping(true);
    try {
      await onSendMessage(CLAUDE_DESIGN_SKIP_MESSAGE);
    } catch {
      setError('Skip could not be sent. Try again.');
    } finally {
      setSkipping(false);
    }
  };

  return (
    <div
      data-testid="claude-design-round-trip-card"
      data-mode={mode}
      className="mx-4 mb-3 rounded-lg p-4"
      style={{ border: '1px solid var(--border)', background: 'var(--surface)', boxShadow: 'var(--shadow-sm)' }}
    >
      <p style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>
        {mode === 'offer' ? 'Optional: Claude Design' : 'Upload your Claude Design bundle'}
      </p>

      {mode === 'offer' && (
        <>
          <p className="mt-1" style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}>
            {CLAUDE_DESIGN_OFFER_INTRO}
          </p>
          <p className="mt-3" style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>Set up Claude Design</p>
          <ol data-testid="claude-design-setup-steps" className="list-decimal ml-5 mt-1 space-y-0.5" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            {CLAUDE_DESIGN_SETUP_STEPS.map(s => <li key={s}>{s}</li>)}
          </ol>
          <p className="mt-3" style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>Bring it back</p>
          <ol data-testid="claude-design-handoff-steps" className="list-decimal ml-5 mt-1 space-y-0.5" style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            {CLAUDE_DESIGN_HANDOFF_STEPS.map(s => <li key={s}>{s}</li>)}
          </ol>
        </>
      )}

      {!readOnly && validProject && (
        <div className="flex flex-wrap items-center gap-2 mt-3">
          {exportEnabled && (
            <>
              <button
                type="button"
                data-testid="claude-design-card-export"
                className={btn}
                style={{ background: 'var(--primary)', color: 'white' }}
                disabled={exporting}
                onClick={() => void handleExport()}
              >
                {exporting ? 'Exporting…' : 'Export ZIP'}
              </button>
              <button
                type="button"
                data-testid="claude-design-card-copy"
                className={btn}
                style={{ border: '1px solid var(--border)', color: 'var(--primary)', background: 'transparent' }}
                onClick={() => void handleCopy()}
              >
                {copied ? 'Copied' : 'Copy PROMPT.md'}
              </button>
            </>
          )}
          {mode === 'offer' && (
            <button
              type="button"
              data-testid="claude-design-card-upload-toggle"
              className={btn}
              style={{ border: '1px solid var(--border)', color: 'var(--primary)', background: 'transparent' }}
              onClick={() => setUploadOpen(o => !o)}
            >
              Upload handoff ZIP
            </button>
          )}
          {mode === 'offer' && (
            // Skip is styled like the export button, not as a lesser link (ruling 4: equally visible).
            <button
              type="button"
              data-testid="claude-design-card-skip"
              className={btn}
              style={{ background: 'var(--primary)', color: 'white' }}
              disabled={skipping}
              onClick={() => void handleSkip()}
            >
              Skip Claude Design
            </button>
          )}
        </div>
      )}

      {error && <p role="alert" className="mt-2" style={{ fontSize: 12, color: 'var(--error)' }}>{error}</p>}

      {!readOnly && validProject && uploadOpen && (
        <div className="mt-3" data-testid="claude-design-card-upload">
          <ClaudeDesignBundleUpload projectId={projectId} onUploaded={() => void onSendMessage(CLAUDE_DESIGN_IMPORTED_MESSAGE)} />
        </div>
      )}
    </div>
  );
}

const ISO_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?Z$/;

/** On reload: the offer card is back when this session's offer was made and not skipped. */
export function claudeDesignCardFromMetadata(metadata: { claudeDesignRoundTrip?: unknown } | null | undefined): 'offer' | null {
  const r = metadata?.claudeDesignRoundTrip as { offeredAt?: unknown; skippedAt?: unknown } | undefined;
  if (!r || typeof r !== 'object') return null;
  const offered = typeof r.offeredAt === 'string' && ISO_RE.test(r.offeredAt);
  const skipped = typeof r.skippedAt === 'string' && ISO_RE.test(r.skippedAt);
  return offered && !skipped ? 'offer' : null;
}

/** The upload-only card shows under the agent's latest reply when it asks for the seed bundle. */
export function wantsClaudeDesignUpload(messages: ReadonlyArray<{ role: string; content: string }>): boolean {
  const last = [...messages].reverse().find(m => m.role === 'assistant');
  return !!last && last.content.includes(CLAUDE_DESIGN_UPLOAD_REQUEST);
}
